import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Award, CheckCircle2, Wallet, UploadCloud, ArrowRight, Lock, Image as ImageIcon } from 'lucide-react';
import { getLocation } from "../utils/GetLocation";

// 1. ESTRUCTURA DE PRECIOS POR PAÍS
// Aquí defines el precio exacto de cada upsell según el país detectado.
const regionalPricing = { 
    co: { 
        currency: "COP", 
        basic: { oldPrice: 50000, price: 29000 },
        pro: { oldPrice: 150000, price: 62000 },
        premium: { oldPrice: 380000, price: 185000 }
    },
    ve: { 
        currency: "Bs", 
        basic: { oldPrice: 5450, price: 4275 },
        pro: { oldPrice: 16340, price: 7499 },
        premium: { oldPrice: 41500, price: 20900 }
    },
    cl: { 
        currency: "CLP", 
        basic: { oldPrice: 12000, price: 7900 },
        pro: { oldPrice: 37000, price: 15000 },
        premium: { oldPrice: 95000, price: 45000 }
    },
    mx: { 
        currency: "MXN", 
        basic: { oldPrice: 220, price: 129 },
        pro: { oldPrice: 660, price: 270 },
        premium: { oldPrice: 1700, price: 800 }
    },
    pe: { 
        currency: "PEN", 
        basic: { oldPrice: 50, price: 29 },
        pro: { oldPrice: 150, price: 60 },
        premium: { oldPrice: 380, price: 175 }
    },
    us: { 
        currency: "USD", 
        basic: { oldPrice: 13, price: 9 },
        pro: { oldPrice: 39, price: 17 },
        premium: { oldPrice: 99, price: 49 }
    }
};

const UpsellPage = () => {
    const navigate = useNavigate();

    // --- ESTADOS ---
    const [selectedPlan, setSelectedPlan] = useState('pro');
    const [fileName, setFileName] = useState("");
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [country, setCountry] = useState("us"); // Por defecto US

    // --- DETECCIÓN DE UBICACIÓN ---
    useEffect(() => {
        getLocation().then(({ countryCode }) => {
            const resolvedCountry = (countryCode || 'us').toLowerCase();
            // Si el país no está en nuestra lista, le asignamos 'us' por defecto
            if (regionalPricing[resolvedCountry]) {
                setCountry(resolvedCountry);
            } else {
                setCountry('us');
            }
        }).catch(() => {
            setCountry('us');
        });
    }, []);

    // Obtenemos los precios activos según el país detectado
    const currentPricing = regionalPricing[country];

    // Base de datos de Upsells fusionada con los precios dinámicos
    const upsellData = {
        basic: { 
            id: 'basic',
            title: "Pack de Grabaciones VIP", 
            desc: "Acceso de por vida a las grabaciones completas de las 2 clases del workshop para que puedas repetirlas y aplicarlas a tu ritmo.",
            oldPrice: currentPricing.basic.oldPrice.toLocaleString("de-DE"),
            price: currentPricing.basic.price.toLocaleString("de-DE")
        },
        pro: { 
            id: 'pro',
            title: "Comunidad Privada + Masterclass PRO", 
            desc: "Acompañamiento continuo durante 6 meses para implementar IA y generar resultados reales.",
            oldPrice: currentPricing.pro.oldPrice.toLocaleString("de-DE"),
            price: currentPricing.pro.price.toLocaleString("de-DE"),
            benefits: [
                "Grabaciones VIP de por vida",
                "Acceso a comunidad privada por 6 meses",
                "1 Masterclass exclusivas (Flow IA)",
                "Soporte y acompañamiento en grupo de WhatsApp durante 3 meses",
                "Entorno de networking y colaboración"
            ]
        },
        premium: { 
            id: 'premium',
            title: "Mentoría 1a1 + Implementación Estratégica", 
            desc: "Sesión privada personalizada para implementar IA y crear tu estrategia de crecimiento digital.",
            oldPrice: currentPricing.premium.oldPrice.toLocaleString("de-DE"),
            price: currentPricing.premium.price.toLocaleString("de-DE"),
            benefits: [
                "Sesión privada por Zoom de 45 minutos",
                "Acceso a masterclass privadas (Flow y HeyGen)",
                "Clase de manejo y optimización de redes sociales",
                "Estrategia personalizada de anuncios ganadores",
                "Revisión estratégica de tu negocio digital"
            ]
        }
    };

    // --- MANEJADORES ---
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
            setFileName(e.target.files[0].name);
        } else {
            setFile(null);
            setFileName("");
        }
    };

    const handleSubmit = () => {
        if (!file) {
            alert("Por favor, adjunta tu comprobante de pago antes de continuar.");
            return;
        }

        setLoading(true);

        // Simulamos la carga
        setTimeout(() => {
            setLoading(false);
            alert(`¡Felicidades! Has adquirido el plan: ${upsellData[selectedPlan].title}.`);
            navigate('/'); // Redirige a inicio o a /success
        }, 3000);
    };

    return (
        <div id="import-course-lp" className="min-h-screen pt-28 pb-12 sm:pt-32 sm:pb-16 relative overflow-x-hidden font-sans bg-gray-900 text-white">
            
            {/* Fondo decorativo */}
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-purple-600 opacity-20 rounded-full blur-[100px] pointer-events-none"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-600 opacity-20 rounded-full blur-[100px] pointer-events-none"></div>

            {/* CONTENEDOR MAESTRO */}
            <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

                {/* Encabezado de la página */}
                <header className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
                    <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-white/10 border border-white/20 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold text-yellow-400 mb-6 shadow-sm">
                        <Award className="w-4 h-4 sm:w-5 sm:h-5" /> ¡Felicidades por completar el Workshop!
                    </div>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 sm:mb-6 leading-tight text-white">
                        No pierdas el impulso. <br/>
                        <span className="text-purple-400" style={{ background: 'linear-gradient(to right, #c084fc, #60a5fa)', WebkitBackgroundClip: 'text', color: 'transparent' }}>
                            Lleva tu aprendizaje al siguiente nivel.
                        </span>
                    </h1>
                    <p className="text-gray-400 text-base sm:text-lg px-2">
                        Selecciona una de las ofertas exclusivas a continuación para acceder al material de estudio y asegurar tus resultados.
                    </p>
                </header>

                {/* Contenedor Principal (Dos Columnas) */}
                <main className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    
                    {/* COLUMNA IZQUIERDA: Opciones de Upsell */}
                    <div className="lg:col-span-7 space-y-6">
                        
                        {/* Opción 1: Básica */}
                        <div 
                            onClick={() => setSelectedPlan('basic')}
                            className={`cursor-pointer border-2 rounded-2xl p-5 sm:p-6 relative transition-all duration-300 transform 
                                ${selectedPlan === 'basic' 
                                    ? 'border-purple-500 bg-gray-800 scale-[1.02] shadow-[0_0_20px_rgba(168,85,247,0.15)]' 
                                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-500'}`}
                        >
                            <div className="flex items-start gap-3 sm:gap-4">
                                <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 mt-1 flex items-center justify-center flex-shrink-0 transition-colors duration-300 
                                    ${selectedPlan === 'basic' ? 'border-purple-500 bg-purple-500' : 'border-gray-500 bg-transparent'}`}>
                                    {selectedPlan === 'basic' && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>}
                                </div>
                                
                                <div className="flex-1 w-full">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 w-full">
                                        <div className="pr-0 sm:pr-4">
                                            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{upsellData.basic.title}</h3>
                                            <p className="text-xs sm:text-sm text-gray-400 mt-1">{upsellData.basic.desc}</p>
                                        </div>
                                        <div className="text-left sm:text-right mt-1 sm:mt-0 shrink-0 whitespace-nowrap">
                                            <span className="block text-xs sm:text-sm text-gray-500 line-through mb-0.5">${upsellData.basic.oldPrice} {currentPricing.currency}</span>
                                            <span className="text-2xl sm:text-3xl font-black text-white">{upsellData.basic.price} <span className="text-xs sm:text-sm font-normal text-gray-400">{currentPricing.currency}</span></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Opción 2: Recomendada */}
                        <div 
                            onClick={() => setSelectedPlan('pro')}
                            className={`cursor-pointer border-2 rounded-2xl p-5 sm:p-6 mt-10 relative transition-all duration-300 transform 
                                ${selectedPlan === 'pro' 
                                    ? 'border-purple-500 bg-gray-800 scale-[1.02] shadow-[0_0_20px_rgba(168,85,247,0.15)]' 
                                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-500'}`}
                        >
                            <div 
                                className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-white text-[10px] sm:text-xs md:text-sm font-bold px-4 sm:px-6 py-1.5 rounded-full uppercase tracking-wider shadow-lg border border-purple-400/50 whitespace-nowrap"
                                style={{ background: 'linear-gradient(to right, #a855f7, #3b82f6)' }}
                            >
                                La más elegida
                            </div>

                            <div className="flex items-start gap-3 sm:gap-4 mt-2">
                                <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 mt-1 flex items-center justify-center flex-shrink-0 transition-colors duration-300 
                                    ${selectedPlan === 'pro' ? 'border-purple-500 bg-purple-500' : 'border-gray-500 bg-transparent'}`}>
                                    {selectedPlan === 'pro' && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>}
                                </div>
                                
                                <div className="flex-1 w-full">
                                    <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-2 w-full">
                                        <div className="pr-0 sm:pr-4">
                                            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{upsellData.pro.title}</h3>
                                            <p className="text-xs sm:text-sm text-gray-400 mt-1">{upsellData.pro.desc}</p>
                                        </div>
                                        <div className="text-left sm:text-right mt-1 sm:mt-0 shrink-0 whitespace-nowrap">
                                            <span className="block text-xs sm:text-sm text-gray-500 line-through mb-0.5">${upsellData.pro.oldPrice} {currentPricing.currency}</span>
                                            <span className="text-2xl sm:text-3xl font-black text-yellow-400">{upsellData.pro.price} <span className="text-xs sm:text-sm font-normal text-gray-400">{currentPricing.currency}</span></span>
                                        </div>
                                    </div>
                                    <ul className="text-xs sm:text-sm text-gray-300 space-y-3 bg-gray-700/30 p-3 sm:p-4 rounded-xl border border-gray-600/30 mt-4">
                                        {upsellData.pro.benefits.map((b, i) => (
                                            <li key={i} className="flex items-start gap-2 sm:gap-3">
                                                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 shrink-0 mt-0.5" /> 
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Opción 3: Premium */}
                        <div 
                            onClick={() => setSelectedPlan('premium')}
                            className={`cursor-pointer border-2 rounded-2xl p-5 sm:p-6 relative transition-all duration-300 transform 
                                ${selectedPlan === 'premium' 
                                    ? 'border-purple-500 bg-gray-800 scale-[1.02] shadow-[0_0_20px_rgba(168,85,247,0.15)]' 
                                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-500'}`}
                        >
                            <div className="flex items-start gap-3 sm:gap-4">
                                <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 mt-1 flex items-center justify-center flex-shrink-0 transition-colors duration-300 
                                    ${selectedPlan === 'premium' ? 'border-purple-500 bg-purple-500' : 'border-gray-500 bg-transparent'}`}>
                                    {selectedPlan === 'premium' && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>}
                                </div>

                                <div className="flex-1 w-full">
                                    <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-2 w-full">
                                        <div className="pr-0 sm:pr-4">
                                            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{upsellData.premium.title}</h3>
                                            <p className="text-xs sm:text-sm text-gray-400 mt-1">{upsellData.premium.desc}</p>
                                        </div>
                                        <div className="text-left sm:text-right mt-1 sm:mt-0 shrink-0 whitespace-nowrap">
                                            <span className="block text-xs sm:text-sm text-gray-500 line-through mb-0.5">${upsellData.premium.oldPrice} {currentPricing.currency}</span>
                                            <span className="text-2xl sm:text-3xl font-black text-white">{upsellData.premium.price} <span className="text-xs sm:text-sm font-normal text-gray-400">{currentPricing.currency}</span></span>
                                        </div>
                                    </div>
                                    
                                    <ul className="text-xs sm:text-sm text-gray-300 space-y-3 bg-gray-700/30 p-3 sm:p-4 rounded-xl border border-gray-600/30 mt-4">
                                        {upsellData.premium.benefits.map((b, i) => (
                                            <li key={i} className="flex items-start gap-2 sm:gap-3">
                                                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 shrink-0 mt-0.5" /> 
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* COLUMNA DERECHA: Checkout y Pago */}
                    <div className="lg:col-span-5 relative mt-6 lg:mt-0">
                        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-5 sm:p-6 md:p-8 shadow-2xl lg:sticky lg:top-8">
                            
                            <h3 className="text-xl sm:text-2xl font-bold mb-5 sm:mb-6 border-b border-gray-700 pb-4">Resumen y Pago</h3>

                            {/* Resumen Dinámico */}
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-6 bg-gray-900/50 p-4 rounded-xl border border-gray-700/50 transition-all duration-300">
                                <div>
                                    <p className="text-gray-400 text-xs sm:text-sm">Estás adquiriendo:</p>
                                    <p className="font-bold text-base sm:text-lg text-white mt-1">
                                        {upsellData[selectedPlan].title}
                                    </p>
                                </div>
                                <div className="text-left sm:text-right shrink-0 whitespace-nowrap">
                                    <p className="text-gray-400 text-xs sm:text-sm">Total a pagar</p>
                                    <p className="font-black text-2xl sm:text-3xl text-yellow-400 mt-1">
                                        {upsellData[selectedPlan].price} <span className="text-sm font-normal text-gray-400">{currentPricing.currency}</span>
                                    </p>
                                </div>
                            </div>

                            {/* Datos de Pago */}
                            <div className="bg-gray-900 p-4 sm:p-5 rounded-xl border border-gray-700 mb-6">
                                <p className="text-xs sm:text-sm text-gray-400 font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Wallet className="w-4 h-4 text-purple-400" /> Datos de Transferencia
                                </p>
                                
                                {country === "ve" && (         
                                <div className="space-y-4 text-xs sm:text-sm">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-800 pb-3 gap-1 sm:gap-0">
                                        <span className="text-gray-400">Banco</span>
                                        <span className="font-mono font-bold text-white text-sm sm:text-base">Provincial (0108)</span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-800 pb-3 gap-1 sm:gap-0">
                                        <span className="text-gray-400">Cédula</span>
                                        <span className="font-mono font-bold text-white text-sm sm:text-base">19241582</span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-1 gap-1 sm:gap-0">
                                        <span className="text-gray-400">Celular</span>
                                        <span className="font-mono font-bold text-white text-sm sm:text-base break-all">04163574963</span>
                                    </div>
                                </div>
                                )}

                                {country === "co" && (         
                                <div className="space-y-4 text-xs sm:text-sm">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-800 pb-3 gap-1 sm:gap-0">
                                        <span className="text-gray-400">Nequi</span>
                                        <span className="font-mono font-bold text-white text-sm sm:text-base">3177169274</span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-1 gap-1 sm:gap-0">
                                        <span className="text-gray-400">Bancolombia Ahorros</span>
                                        <span className="font-mono font-bold text-white text-sm sm:text-base">00689258018</span>
                                    </div>
                                </div>
                                )}

                                {country !== "ve" && country !== "co" && (         
                                <div className="space-y-4 text-xs sm:text-sm">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-1 gap-1 sm:gap-0">
                                        <span className="text-gray-400">Binance Pay (Email)</span>
                                        <span className="font-mono font-bold text-white text-sm sm:text-base break-all">pagos@dissempacademy.com</span>
                                    </div>
                                </div>
                                )}
                            </div>

                            {/* Carga de Comprobante */}
                            <div className="mb-6">
                                <label className="block text-xs sm:text-sm text-gray-400 font-semibold mb-3">Sube tu comprobante de pago</label>
                                
                                <label className={`flex flex-col items-center justify-center w-full h-28 sm:h-32 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 
                                    ${file ? 'border-purple-500 bg-purple-500/10' : 'border-gray-600 hover:bg-gray-700 hover:border-purple-500'}`}>
                                    
                                    <div className="flex flex-col items-center justify-center pt-4 sm:pt-5 pb-5 sm:pb-6 px-4">
                                        {file ? (
                                            <div className="flex flex-col items-center">
                                                <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 mb-2" />
                                                <p className="text-xs sm:text-sm text-yellow-400 font-medium text-center break-all line-clamp-2">{fileName}</p>
                                            </div>
                                        ) : (
                                            <>
                                                <UploadCloud className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mb-2 sm:mb-3" />
                                                <p className="text-xs sm:text-sm text-gray-300 font-medium text-center">Haz clic para adjuntar imagen</p>
                                                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">JPG, PNG o PDF</p>
                                            </>
                                        )}
                                    </div>
                                    <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                                </label>

                            </div>

                            {/* Botón de Acción */}
                            <button 
                                onClick={handleSubmit} 
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-400 hover:to-blue-400 text-white font-extrabold text-base sm:text-lg py-4 sm:py-5 rounded-xl shadow-[0_0_20px_rgba(168,85,247,0.3)] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Adquirir Beneficio Extra <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                            
                            <p className="text-center text-xs text-gray-500 mt-4 sm:mt-5 flex items-center justify-center gap-1">
                                <Lock className="w-3 h-3" /> Proceso seguro y encriptado
                            </p>

                        </div>
                    </div>

                </main>
            </div>

            {/* Overlay de Carga ABSOLUTO */}
            {loading && (
                <div className="fixed inset-0 bg-gray-900/95 z-[200] flex flex-col items-center justify-center transition-opacity duration-300 backdrop-blur-sm px-4 text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 border-[6px] border-gray-700 border-t-purple-500 rounded-full animate-spin mb-4 sm:mb-6 shadow-[0_0_30px_rgba(168,85,247,0.3)]"></div>
                    <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">Procesando tu solicitud...</h2>
                    <p className="text-sm sm:text-lg text-gray-400">Validando comprobante y generando acceso.</p>
                </div>
            )}

        </div>
    );
};

export default UpsellPage;